import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { CODES } from "../consts/codes";
import { GetUserByEmail } from "../services/user/userServices";
import jwt_decode from "jwt-decode";

/**
 * Slice de Redux para el estado del usuario autenticado.
 * Maneja la información del usuario, menú de navegación y primer ingreso.
 */
export const userSlice = createSlice({
  name: "user",
  initialState: {
    user: "",
    status: "idle",
    error: null,
  },
  reducers: {
    /**
     * Reinicia el estado del usuario a "idle".
     */
    resetUserStatus(state) {
      state.status = "idle";
    },
    /**
     * Establece el estado del menú de navegación de primer nivel.
     * @param {Object} state - Estado actual de Redux.
     * @param {Object} action - Acción con el payload booleano.
     */
    setOpenMenuLevelOne(state, action) {
      if (state.user) {
        state.user.openMenuLevelOne = action.payload;
      }
    },
    /**
     * Establece el estado del menú de navegación de segundo nivel.
     * @param {Object} state - Estado actual de Redux.
     * @param {Object} action - Acción con el payload booleano.
     */
    setOpenMenuLevelTwo(state, action) {
      if (state.user) {
        state.user.openMenuLevelTwo = action.payload;
      }
    },
    /**
     * Establece el indicador de primera navegación del usuario.
     * @param {Object} state - Estado actual de Redux.
     * @param {Object} action - Acción con el payload booleano.
     */
    setFirstNavigate(state, action) {
      if (state.user) {
        state.user.firstNavigate = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUser.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchUser.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.user = {
          ...action.payload.data,
          openMenuLevelOne: false,
          openMenuLevelTwo: false,
          firstNavigate: true,
        };
      })
      .addCase(fetchUser.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      });
  },
});

// GETTERS
/**
 * Obtiene la información del usuario autenticado.
 * @param {Object} state - Estado global de Redux.
 * @returns {Object|null} Datos del usuario o null.
 */
export const getUser = (state) => state.user.user;

/**
 * Obtiene el estado de la petición del usuario.
 * @param {Object} state - Estado global de Redux.
 * @returns {string} Estado actual ("idle", "loading", "succeeded", "failed").
 */
export const getUserStatus = (state) => state.user.status;

// ACTIONS
export const { resetUserStatus, setOpenMenuLevelOne, setOpenMenuLevelTwo, setFirstNavigate } =
  userSlice.actions;

// FETCHERS
/**
 * Async thunk que obtiene los datos del usuario autenticado
 * usando el token JWT almacenado en localStorage.
 * @returns {Promise<Object>} Objeto con estado y datos del usuario.
 */
export const fetchUser = createAsyncThunk("user/fetchuser", async () => {
  const jwtTokenInfo = localStorage.getItem("infoToken");
  let email = "";
  if (jwtTokenInfo) {
    const jwtDecodeTokenInfo = jwt_decode(jwtTokenInfo);
    email = jwtDecodeTokenInfo.email;
  }
  const obj = { email };
  const response = await GetUserByEmail(obj);
  if (response.status === CODES.COD_RESPONSE_HTTP_OK) {
    return {
      status: response.status,
      data: {
        ...response.data.data,
        openMenuLevelOne: false,
        openMenuLevelTwo: false,
        firstNavigate: true,
      },
    };
  }
});

/**
 * Exportación por defecto del reducer del slice de usuario.
 */
export default userSlice.reducer;